import {
  type Except,
  translationUtils,
  type ValidatedKey,
} from '@festina-life/core'
import { useField } from 'formik'

import { Checkbox, type CheckboxProps } from '../../forms/checkbox/Checkbox'
import { useFormikErrorWithState } from '../../hooks/useFormikErrorWithState'
import { useFormikIsDisabled } from '../../hooks/useFormikIsDisabled'
import { useFormikIsRequired } from '../../hooks/useFormikIsRequired'
import { useTranslate } from '../../hooks/useTranslate'
import { useExistingValuesContext } from '../../theme/existingValuesContext'
import {
  handleDefaultValueClick,
  handleExistingValueClick,
} from '../formikUtils/formikUtils'
import { getWrapperProps } from '../formikUtils/getWrapperProps'
import { useSimpleValueDifferenceCheck } from '../formikUtils/useSimpleValueDifferenceCheck'
import { withFormikInputDiffing } from '../formikUtils/withFormikInputDiffing'
import type { FormikInputPropsForWrapper } from '../types'

export type FormikCheckboxProps = Except<
  CheckboxProps,
  'defaultChecked' | 'onChange' | 'onBlur' | 'wrapperProps'
> & {
  name: ValidatedKey
  isRequired?: boolean
  defaultValue?: boolean
  onBlur?: (value: boolean | undefined) => void
} & Except<FormikInputPropsForWrapper, 'validationText'>

/**
 * FormikCheckboxInput to be used within the scope of Formik forms. Will extract the field's data from Formik and surround the
 * checkbox element with a FormInput to show the field status consistently.
 */
const FormikCheckboxInternal = ({
  name,
  isRequired,
  label,
  defaultValue,
  onDefaultValueClick,
  onBlur,
  description,
  disabled,
  hasRightColumnAlways,
  className,
  existingValueConfig,
  ...restProps
}: FormikCheckboxProps) => {
  const [field, meta, { setValue, setTouched }] = useField({
    name,
    type: 'checkbox',
  })
  const { isInError, error } = useFormikErrorWithState(meta)
  const isFieldDisabled = useFormikIsDisabled(disabled)
  const translate = useTranslate()

  // The headless UI checkbox is a span so ,does not provide the value from the events on blur and etc. So we pull it from formik
  const value = field.value as boolean | undefined
  const existingValue = useExistingValuesContext()?.getExistingValue?.(
    name,
  ) as boolean

  const inputValueDifferenceFromExistingValue =
    useSimpleValueDifferenceCheck(name)

  const wrapperProps = getWrapperProps({
    label,
    existingValue: existingValue
      ? translationUtils.translateBoolean(existingValue, translate)
      : undefined,
    existingValueConfig,
    inputValueDifferenceFromExistingValue,
    commonWrapperProps: {
      isRequired: useFormikIsRequired(name, isRequired),
      description,
      defaultValue:
        defaultValue !== undefined
          ? translationUtils.translateBoolean(defaultValue, translate)
          : undefined,
      onDefaultValueClick: () =>
        handleDefaultValueClick({
          defaultValue,
          onBlur,
          onDefaultValueClick,
        }),
      onExistingValueClick: () =>
        handleExistingValueClick({
          existingValue,
          setValue,
          onBlur,
        }),
      htmlFor: name,
      error,
      hasRightColumnAlways,
      className,
    },
  })

  return (
    <Checkbox
      wrapperProps={wrapperProps}
      id={name}
      isInError={isInError}
      disabled={isFieldDisabled}
      {...field}
      {...restProps}
      onChange={setValue}
      onBlur={async () => {
        await setTouched(true)
        onBlur?.(value)
        return undefined
      }}
    />
  )
}

export const FormikCheckbox = withFormikInputDiffing(FormikCheckboxInternal)
